(() => {
  'use strict';

  const EVENT_NAME = 'ls_add_to_cart_source';
  const CART_ADD_PATH = /\/cart\/add(?:\.js)?(?:\?|$)/;
  const CONTEXT_MAX_AGE_MS = 10000;
  const CONTEXT_REUSE_MS = 750;
  const VALID_SOURCES = new Set([
    'product_page_main_button',
    'homepage_featured_product',
    'homepage_quick_add',
    'collection_quick_add',
    'search_result',
    'shop_for_packs',
    'pack_switcher',
    'recommended_product',
    'cart_upsell',
    'other',
  ]);

  const originalFetch = window.fetch.bind(window);
  let lastContext = null;
  const recentEvents = new Map();
  const recordedEvents = [];
  const cartQuantities = new Map();
  const pendingCartWatches = new Map();
  document.documentElement.dataset.lsAtcTrackerReady = 'true';

  function pageType() {
    const value = document.body?.dataset.lsPageType;
    return ['home', 'product', 'collection', 'search', 'custom_page', 'cart'].includes(value)
      ? value
      : 'custom_page';
  }

  function currency() {
    return document.body?.dataset.lsCurrency || window.Shopify?.currency?.active || 'GBP';
  }

  function closestElement(target) {
    if (target instanceof Element) return target;
    if (target instanceof Node) return target.parentElement;
    return null;
  }

  function sourceFor(element) {
    const type = pageType();
    const recommended = element?.closest(
      '[data-ls-atc-source="recommended_product"], product-recommendations, .complementary-products'
    );
    if (recommended) return 'recommended_product';

    const explicitElement = element?.closest('[data-ls-atc-source]');
    const explicit = explicitElement?.dataset.lsAtcSource;
    const insideQuickAddModal = Boolean(element?.closest('quick-add-modal'));

    if (
      explicit &&
      VALID_SOURCES.has(explicit) &&
      !(insideQuickAddModal && explicit === 'product_page_main_button')
    ) {
      return explicit;
    }

    if (element?.closest('#CartDrawer, cart-drawer, .cart-drawer, .cart__footer, [data-cart-upsell]')) {
      return 'cart_upsell';
    }

    if (type === 'product' && !insideQuickAddModal) return 'product_page_main_button';
    if (type === 'home') return 'homepage_quick_add';
    if (type === 'collection') return 'collection_quick_add';
    if (type === 'search') return 'search_result';
    return 'other';
  }

  function contextFor(target) {
    const element = closestElement(target);
    const dataElement = element?.closest(
      '[data-ls-product-id], product-form, quick-add-bulk, quick-order-list, form[action*="/cart/add"]'
    );
    const productForm = element?.closest('form[action*="/cart/add"]') || dataElement?.querySelector?.('form[action*="/cart/add"]');
    const selectedVariantId = productForm?.querySelector('[name="id"]')?.value || '';

    return {
      capturedAt: Date.now(),
      eventId: '',
      sourceComponent: sourceFor(element),
      productId: dataElement?.dataset.lsProductId || '',
      variantId: selectedVariantId || dataElement?.dataset.lsVariantId || '',
      productTitle: dataElement?.dataset.lsProductTitle || '',
    };
  }

  function rememberContext(event) {
    const element = closestElement(event.target);
    if (!element) return;

    if (
      !element.closest(
        'product-form, quick-add-bulk, quick-order-list, form[action*="/cart/add"], [data-ls-atc-source]'
      )
    ) {
      return;
    }

    const nextContext = contextFor(event.submitter || element);
    const canReuseOperation =
      lastContext &&
      Date.now() - lastContext.capturedAt <= CONTEXT_REUSE_MS &&
      lastContext.sourceComponent === nextContext.sourceComponent &&
      lastContext.variantId === nextContext.variantId;

    nextContext.eventId = canReuseOperation ? lastContext.eventId : eventId();
    lastContext = nextContext;
  }

  function resolvedContext(target) {
    const freshContext = contextFor(target);
    const canUseRecent =
      lastContext &&
      Date.now() - lastContext.capturedAt <= CONTEXT_MAX_AGE_MS &&
      lastContext.sourceComponent === freshContext.sourceComponent &&
      (!freshContext.variantId || lastContext.variantId === freshContext.variantId);

    if (canUseRecent) return { ...lastContext };
    freshContext.eventId = eventId();
    return freshContext;
  }

  document.addEventListener('submit', rememberContext, true);
  document.addEventListener('click', rememberContext, true);
  document.addEventListener('change', rememberContext, true);

  function sleep(milliseconds) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  async function readCart() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await originalFetch(`${root}cart.js`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (_) {
      return null;
    }
  }

  function quantityForVariant(cart, variantId) {
    return (cart?.items || [])
      .filter((item) => numericId(item.variant_id || item.id) === variantId)
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
  }

  function updateCartSnapshot(cart) {
    cartQuantities.clear();
    for (const item of cart?.items || []) {
      const variantId = numericId(item.variant_id || item.id);
      cartQuantities.set(variantId, (cartQuantities.get(variantId) || 0) + Number(item.quantity || 0));
    }
  }

  let cartSnapshotReady = readCart().then((cart) => {
    if (cart) updateCartSnapshot(cart);
    document.documentElement.dataset.lsAtcCartSnapshotReady = cart ? 'true' : 'false';
    return cart;
  });

  function isManualAddTrigger(event) {
    const element = closestElement(event.target);
    if (!element) return false;

    if (event.type === 'submit') {
      return Boolean(element.closest?.('form[action*="/cart/add"]') || element.matches?.('form[action*="/cart/add"]'));
    }

    const button = element.closest('button, [role="button"], input[type="submit"]');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return false;
    return Boolean(button.closest('form[action*="/cart/add"], product-form, quick-add-bulk, quick-order-list'));
  }

  async function watchForVerifiedCartIncrease(target) {
    const context = resolvedContext(target);
    const variantId = numericId(context.variantId);
    if (!variantId) return;

    await cartSnapshotReady;
    const previousQuantity = cartQuantities.get(variantId) || 0;
    const watchKey = `${variantId}|${context.sourceComponent}`;
    if (pendingCartWatches.has(watchKey)) return;
    pendingCartWatches.set(watchKey, Date.now());

    try {
      for (const delay of [550, 900, 1400]) {
        await sleep(delay);
        const cart = await readCart();
        if (!cart) continue;

        const currentQuantity = quantityForVariant(cart, variantId);
        const addedQuantity = currentQuantity - previousQuantity;
        const matchingItem = (cart.items || []).find(
          (item) => numericId(item.variant_id || item.id) === variantId
        );
        updateCartSnapshot(cart);

        if (addedQuantity > 0 && matchingItem) {
          processPayload(
            {
              ...matchingItem,
              quantity: addedQuantity,
              line_price: undefined,
              final_line_price: undefined,
            },
            {
              items: [{ id: variantId, quantity: addedQuantity, properties: {} }],
              context,
            }
          );
          return;
        }
      }
    } finally {
      pendingCartWatches.delete(watchKey);
    }
  }

  function beginVerifiedCartWatch(event) {
    if (!isManualAddTrigger(event)) return;
    watchForVerifiedCartIncrease(event.submitter || event.target);
  }

  document.addEventListener('click', beginVerifiedCartWatch, true);
  document.addEventListener('submit', beginVerifiedCartWatch, true);

  function safeJson(value) {
    try {
      return JSON.parse(value);
    } catch (_) {
      return null;
    }
  }

  function valueFromFormData(formData, name) {
    const value = formData.get(name);
    return value == null ? '' : String(value);
  }

  function requestItems(body) {
    let parsed = null;

    if (body instanceof FormData) {
      const properties = {};
      for (const [key, value] of body.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = String(value);
      }

      return [
        {
          id: valueFromFormData(body, 'id'),
          quantity: Number(valueFromFormData(body, 'quantity') || 1),
          properties,
        },
      ];
    }

    if (body instanceof URLSearchParams) {
      return [
        {
          id: body.get('id') || '',
          quantity: Number(body.get('quantity') || 1),
          properties: {},
        },
      ];
    }

    if (typeof body === 'string') parsed = safeJson(body);
    else if (body && typeof body === 'object') parsed = body;

    if (!parsed) return [];
    if (Array.isArray(parsed.items)) return parsed.items;

    return [
      {
        id: parsed.id || '',
        quantity: Number(parsed.quantity || 1),
        properties: parsed.properties || {},
      },
    ];
  }

  function isFreeGift(item) {
    const properties = item?.properties || {};
    return String(properties._free_gift || '').toLowerCase() === 'true';
  }

  function requestDetails(input, init = {}) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = String(init.method || input?.method || 'GET').toUpperCase();
    if (method !== 'POST' || !CART_ADD_PATH.test(url)) return null;

    const items = requestItems(init.body);
    if (items.length && items.every(isFreeGift)) return null;

    const recentContext =
      lastContext && Date.now() - lastContext.capturedAt <= CONTEXT_MAX_AGE_MS
        ? { ...lastContext }
        : resolvedContext(document.activeElement);

    return {
      items: items.filter((item) => !isFreeGift(item)),
      context: recentContext,
    };
  }

  function responseItems(payload) {
    if (!payload || payload.status || payload.errors || payload.description) return [];
    if (Array.isArray(payload.items)) return payload.items;
    if (payload.item) return [payload.item];
    if (payload.id || payload.variant_id || payload.product_id) return [payload];
    return [];
  }

  function numericId(value) {
    if (value == null) return '';
    return String(value).replace(/^gid:\/\/shopify\/[A-Za-z]+\//, '');
  }

  function totalValue(item, quantity) {
    const lineValue = Number(item.final_line_price ?? item.line_price);
    if (Number.isFinite(lineValue)) return Number((lineValue / 100).toFixed(2));

    const unitValue = Number(item.final_price ?? item.price);
    if (Number.isFinite(unitValue)) return Number(((unitValue * quantity) / 100).toFixed(2));
    return 0;
  }

  function eventId() {
    if (window.crypto?.randomUUID) return `lsatc-${window.crypto.randomUUID()}`;
    return `lsatc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  function publish(payload) {
    const fingerprint = payload.event_id;
    if (recentEvents.has(fingerprint)) return;
    recentEvents.set(fingerprint, Date.now());

    recordedEvents.push(payload);
    document.documentElement.dataset.lsAtcEventCount = String(recordedEvents.length);
    document.documentElement.dataset.lsLastAtcSource = payload.source_component;
    document.documentElement.dataset.lsLastAtcEventId = payload.event_id;
    document.documentElement.dataset.lsLastAtcPayload = JSON.stringify(payload);
    window.dispatchEvent(new CustomEvent('ls:add-to-cart-source', { detail: payload }));
  }

  function processPayload(payload, details) {
    if (!details) return;
    const addedItems = responseItems(payload);
    if (!addedItems.length) return;

    const requestedVariantIds = new Set(details.items.map((item) => numericId(item.id)).filter(Boolean));
    const matchingItems = requestedVariantIds.size
      ? addedItems.filter((item) => requestedVariantIds.has(numericId(item.variant_id || item.id)))
      : addedItems;

    for (const item of matchingItems) {
      const requestedItem = details.items.find(
        (candidate) => numericId(candidate.id) === numericId(item.variant_id || item.id)
      );
      const quantity = Number(item.quantity || requestedItem?.quantity || 1);
      const timestamp = new Date().toISOString();

      publish({
        source_page_path: window.location.pathname,
        source_page_type: pageType(),
        source_component: details.context.sourceComponent || 'other',
        product_id: numericId(item.product_id || details.context.productId),
        variant_id: numericId(item.variant_id || item.id || details.context.variantId),
        product_title: String(item.product_title || item.title || details.context.productTitle || '').slice(0, 150),
        quantity,
        value: totalValue(item, quantity),
        currency: currency(),
        event_time: timestamp,
        event_id: details.context.eventId || eventId(),
      });
    }
  }

  async function processSuccessfulAdd(response, details) {
    if (!response.ok || !details) return;

    let payload;
    try {
      payload = await response.clone().json();
    } catch (_) {
      return;
    }

    processPayload(payload, details);
  }

  function trackSuccess({ payload, body, element }) {
    const items = requestItems(body).filter((item) => !isFreeGift(item));
    if (!items.length) return;

    processPayload(payload, {
      items,
      context: resolvedContext(element),
    });
  }

})();
